// STEP IG-S1 · Instagram Business Login 계정 연결 카드
// - TikTokAccountCard 패턴 미러링. 기존 short-form 파이프라인 재사용.
// - 이 카드는 판매자 본인 Instagram 계정 OAuth 연결만 담당.
// - Reels 자동수집은 별도 (기존 short_clips + auto-scan 파이프라인 재사용).
import React, { useEffect, useState, useCallback } from 'react';
import { Instagram, CheckCircle2, Link2, LogOut, AlertCircle, RefreshCw, Radio, Search } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function InstagramAccountCard({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  // STEP IG-LIVE-DETECT-P1 · Instagram LIVE 자동감지 상태 (foundation only)
  const [liveDetect, setLiveDetect] = useState(null);
  const [liveDetectBusy, setLiveDetectBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, c, ld] = await Promise.all([
        api.get('/seller/instagram/status'),
        api.get('/instagram/config'),
        api.get('/seller/instagram/live/auto-detect').catch(() => ({ data: null })),
      ]);
      setStatus(s.data || null);
      setConfig(c.data || null);
      setLiveDetect(ld.data || null);
    } catch { /* silent · unauthenticated */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleLiveDetect = async (next) => {
    setLiveDetectBusy(true);
    try {
      await api.patch('/seller/instagram/live/auto-detect', { enabled: next });
      toast.success(next ? 'Instagram LIVE 자동감지 ON' : 'Instagram LIVE 자동감지 OFF');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '변경 실패');
    } finally { setLiveDetectBusy(false); }
  };

  const runLiveDetectNow = async () => {
    setLiveDetectBusy(true);
    try {
      const r = await api.post('/seller/instagram/live/auto-detect/tick');
      const st = r?.data?.status;
      if (st === 'live_found') toast.success('LIVE 감지됨');
      else if (st === 'no_live') toast.info('현재 LIVE 방송 없음');
      else if (st === 'just_ended') toast.info('LIVE 종료 감지됨');
      else if (st === 'feature_disabled') toast.warning('Meta 승인 대기 중 · 실제 조회 미실행');
      else toast.info(`상태: ${st}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '검색 실패');
    } finally { setLiveDetectBusy(false); }
  };

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('ig_connected') === '1') {
      toast.success('Instagram 연결 완료 · 권한 상태를 확인하세요.');
      const u = new URL(window.location.href);
      u.searchParams.delete('ig_connected');
      window.history.replaceState({}, '', u.toString());
      load();
    } else if (p.get('ig_error')) {
      const err = p.get('ig_error');
      const msg = {
        not_configured: 'Instagram API 설정이 아직 완료되지 않았습니다.',
        invalid_state: 'OAuth 상태값이 유효하지 않습니다. 다시 시도해주세요.',
        invalid_response: 'Instagram 응답이 잘못되었습니다.',
        token_exchange_failed: 'Instagram 토큰 교환 실패 · 다시 시도해주세요.',
        long_lived_failed: 'Instagram 장기 토큰 발급 실패',
        save_failed: 'Instagram 계정 저장 실패',
      }[err] || `Instagram 연결 실패: ${err}`;
      toast.error(msg);
      const u = new URL(window.location.href);
      u.searchParams.delete('ig_error');
      window.history.replaceState({}, '', u.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get('/instagram/oauth/start');
      if (r.data?.auth_url) window.location.href = r.data.auth_url;
      else toast.error('OAuth 시작 URL 을 생성할 수 없습니다.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Instagram 연결 시작 실패');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Instagram 계정 연결을 해제하시겠습니까?')) return;
    setBusy(true);
    try {
      await api.post('/seller/instagram/oauth/disconnect');
      toast.success('Instagram 계정 연결 해제됨');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '연결 해제 실패');
    } finally { setBusy(false); }
  };

  if (!status || !config) return null;
  const oauthReady = !!config.oauth?.configured;
  const connected = !!status.connected;

  return (
    <div className="rounded-xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#141414] p-3 md:p-4"
      data-testid="instagram-account-card">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white">
          <Instagram size={14}/>
        </span>
        <p className="text-sm font-black">Instagram</p>
        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded ${
          connected ? 'bg-emerald-500/10 text-emerald-700' : 'bg-gray-500/10 text-gray-500'
        }`} data-testid="instagram-account-status">
          {connected ? '● 계정 연결됨' : '○ 계정 연결 필요'}
        </span>
        {!oauthReady && (
          <span className="text-[10px] font-black px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700 inline-flex items-center gap-1"
            data-testid="instagram-not-configured">
            <AlertCircle size={10}/> API 설정 미완료
          </span>
        )}
      </div>

      {connected ? (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-orange-400 text-white flex items-center justify-center">
            <Instagram size={18}/>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black truncate" data-testid="instagram-username">
              @{status.username || '(username 미제공)'}
            </p>
            <div className="mt-0.5 flex items-center gap-1 flex-wrap">
              {(status.webhook_subscribed_fields || []).length > 0 ? (
                <span
                  className="inline-flex items-center gap-1 text-[9px] font-black text-emerald-700 bg-emerald-500/10 px-1.5 py-0.5 rounded-full"
                  data-testid="instagram-webhook-badge-on"
                  title={`Webhook subscribed: ${(status.webhook_subscribed_fields || []).join(', ')}${status.webhook_subscribed_at ? ' · ' + new Date(status.webhook_subscribed_at).toLocaleDateString() : ''}`}
                >
                  <CheckCircle2 size={9}/> Webhook 연결됨
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 text-[9px] font-black text-amber-700 bg-amber-500/10 px-1.5 py-0.5 rounded-full"
                  data-testid="instagram-webhook-badge-off"
                  title={status.webhook_last_error || 'live_comments Webhook 미구독'}
                >
                  <AlertCircle size={9}/> Webhook 미구독
                </span>
              )}
            </div>
            <p className="text-[10px] text-gray-500 inline-flex items-center gap-1 mt-0.5">
              <CheckCircle2 size={10} className="text-emerald-500"/>
              {status.reconnect_required ? '추가 권한 동의 필요' : '연결 권한 확인됨'}{status.account_type ? ` · ${status.account_type}` : ''} · scope: {(status.scope || '').replace(/,/g, ' · ')}
            </p>
          </div>
          <button onClick={load} disabled={busy}
            className="p-1.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-gray-400"
            data-testid="instagram-refresh" title="새로고침">
            <RefreshCw size={12} className={busy ? 'animate-spin' : ''}/>
          </button>
          {status.reconnect_required && (
            <button onClick={connect} disabled={busy || !oauthReady}
              className="px-3 py-1.5 rounded-full text-[11px] font-bold border border-amber-400 text-amber-700 disabled:opacity-50"
              data-testid="instagram-reconnect">
              추가 권한 동의·재연결
            </button>
          )}
          <button onClick={disconnect} disabled={busy}
            className="px-3 py-1.5 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1 disabled:opacity-50"
            data-testid="instagram-disconnect">
            <LogOut size={11}/> 연결 해제
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-[11px] text-gray-500 leading-relaxed">
            Instagram Business/Creator 계정을 연결하면 판매자 본인의 공개 Reels 를 LIVEMOA 에 자동으로 가져올 수 있습니다.
            <br/>계정 조회·Reels 발행·댓글 관리에 필요한 권한을 Instagram 동의 화면에서 확인하세요.
          </p>
          <button onClick={connect} disabled={busy || !oauthReady}
            className="px-4 py-2 rounded-full text-sm font-black bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="instagram-connect">
            <Link2 size={14}/>
            {oauthReady ? 'Instagram 계정 연결' : 'API 설정 미완료 · 관리자 문의'}
          </button>
          {!oauthReady && config.oauth?.notice && (
            <p className="text-[10px] text-amber-700 dark:text-amber-400" data-testid="instagram-oauth-notice">
              {config.oauth.notice}
            </p>
          )}
        </div>
      )}

      {/* STEP IG-LIVE-DETECT-P1 · Instagram LIVE 자동 감지 (foundation only) */}
      {connected && liveDetect && (
        <div className="mt-3 pt-3 border-t border-black/5 dark:border-white/10"
             data-testid="instagram-live-autodetect-section">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1 text-[11px] font-black text-gray-700 dark:text-gray-200">
              <Radio size={12} className="text-fuchsia-500"/> Instagram LIVE 자동 감지
            </span>
            {!liveDetect.feature_enabled ? (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-700"
                    data-testid="instagram-live-autodetect-pending">
                Meta 승인 대기 · foundation only
              </span>
            ) : liveDetect.enabled ? (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-700"
                    data-testid="instagram-live-autodetect-on">
                ● ON
              </span>
            ) : (
              <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-500"
                    data-testid="instagram-live-autodetect-off">
                ○ OFF
              </span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => toggleLiveDetect(!liveDetect.enabled)}
              disabled={liveDetectBusy || !liveDetect.feature_enabled}
              className={`px-3 py-1 rounded-full text-[11px] font-bold border inline-flex items-center gap-1 disabled:opacity-40 ${
                liveDetect.enabled
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'border-black/10 dark:border-white/10 text-gray-700 dark:text-gray-200'
              }`}
              data-testid="instagram-live-autodetect-toggle"
              title={liveDetect.feature_enabled
                ? '자동 감지 ON/OFF'
                : 'Meta Advanced Access 승인 후 활성화됩니다.'}
            >
              {liveDetect.enabled ? 'ON' : 'OFF'}
            </button>
            <button
              onClick={runLiveDetectNow}
              disabled={liveDetectBusy}
              className="px-3 py-1 rounded-full text-[11px] font-bold border border-black/10 dark:border-white/10 inline-flex items-center gap-1 disabled:opacity-40"
              data-testid="instagram-live-autodetect-now"
            >
              <Search size={11}/> 지금 검색
            </button>
            {liveDetect.last_status && (
              <span className="text-[10px] text-gray-500" data-testid="instagram-live-autodetect-last">
                최근: {liveDetect.last_status}
              </span>
            )}
          </div>
          {liveDetect.notice && (
            <p className="mt-1.5 text-[10px] text-amber-700 dark:text-amber-400"
               data-testid="instagram-live-autodetect-notice">
              {liveDetect.notice}
            </p>
          )}
        </div>
      )}

      {!compact && (
        <p className="mt-3 text-[10px] text-gray-400 leading-relaxed">
          ※ Instagram 댓글/DM 등 추가 기능은 별도 심사 후 진행됩니다. 현재 이 카드는 <b>계정 연결(Reels 연동 준비)</b> 만 담당합니다.
        </p>
      )}
    </div>
  );
}

